import { useState } from "react";

import { useTranslation } from "react-i18next";

import { axisStep, showAxisLabel } from "./axis";
import { useMeasuredWidth } from "./measure";

export interface PercentDatum {
  key: string;
  label: string;
  /** `null` — shu nuqtada o'lchov yo'q (masalan, haftada dars bo'lmagan). */
  value: number | null;
  detail?: string;
}

/** O'lchashdan oldingi (SSR, test) kenglik. */
const FALLBACK_WIDTH = 520;
const HEIGHT = 200;
const PADDING = { top: 12, right: 12, bottom: 28, left: 36 };
const GRID = [0, 50, 100];

/**
 * Foiz chizig'i — o'q HAR DOIM 0–100.
 *
 * Avtomatik masshtab 85% va 90% orasini butun balandlikka cho'zib,
 * arzimas tebranishni "qulash" qilib ko'rsatardi. Davomat va baho foizi
 * uchun to'liq shkala — halol shkala.
 *
 * `null` nuqtada chiziq UZILADI: dars bo'lmagan hafta "0% davomat" emas.
 */
export const PercentLine = ({ data, ariaLabel }: { data: PercentDatum[]; ariaLabel: string }) => {
  const { t } = useTranslation();
  const [active, setActive] = useState<number | null>(null);
  const [frameRef, width] = useMeasuredWidth<HTMLDivElement>(FALLBACK_WIDTH);

  if (data.length === 0) return null;

  const plotWidth = width - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const x = (index: number) =>
    PADDING.left + (data.length === 1 ? plotWidth / 2 : (index / (data.length - 1)) * plotWidth);
  const y = (value: number) =>
    PADDING.top + plotHeight - (Math.min(Math.max(value, 0), 100) / 100) * plotHeight;

  // Uzluksiz bo'laklar: har bir `null` yangi chiziq boshlaydi.
  const segments: string[] = [];
  let run: string[] = [];
  data.forEach((item, index) => {
    if (item.value === null) {
      if (run.length) segments.push(run.join(" "));
      run = [];
      return;
    }
    run.push(`${x(index)},${y(item.value)}`);
  });
  if (run.length) segments.push(run.join(" "));

  const hitWidth = data.length === 1 ? plotWidth : plotWidth / (data.length - 1);
  const labelStep = axisStep(data.length);
  const current = active === null ? undefined : data[active];
  const valueText = (value: number | null) =>
    value === null ? t("REPORTS.NO_MEASURE") : `${Math.round(value)}%`;

  return (
    <div ref={frameRef} className="flex flex-col gap-2">
      <svg
        viewBox={`0 0 ${width} ${HEIGHT}`}
        width="100%"
        height={HEIGHT}
        className="block"
        role="img"
        aria-label={ariaLabel}
      >
        {GRID.map(level => (
          <g key={level}>
            <line
              x1={PADDING.left}
              x2={width - PADDING.right}
              y1={y(level)}
              y2={y(level)}
              stroke="var(--border)"
              strokeWidth={1}
              strokeDasharray={level === 0 ? undefined : "2 4"}
            />
            <text
              x={PADDING.left - 6}
              y={y(level) + 3}
              textAnchor="end"
              className="fill-muted-foreground text-[11px] tabular-nums"
            >
              {level}%
            </text>
          </g>
        ))}

        {segments.map(points => (
          <polyline
            key={points}
            points={points}
            fill="none"
            stroke="var(--chart-1)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {data.map((item, index) =>
          item.value === null ? null : (
            <circle
              key={`dot-${item.key}`}
              cx={x(index)}
              cy={y(item.value)}
              r={active === index ? 5 : 4}
              fill="var(--chart-1)"
              stroke="var(--card)"
              strokeWidth={2}
            />
          ),
        )}

        {data.map((item, index) => (
          <rect
            key={`hit-${item.key}`}
            x={x(index) - hitWidth / 2}
            y={PADDING.top}
            width={hitWidth}
            height={plotHeight}
            fill="transparent"
            tabIndex={0}
            role="button"
            aria-label={`${item.label}: ${valueText(item.value)}`}
            onMouseEnter={() => setActive(index)}
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive(index)}
            onBlur={() => setActive(null)}
          />
        ))}

        {data.map((item, index) => (
          <text
            key={`label-${item.key}`}
            opacity={showAxisLabel(index, data.length, labelStep) ? 1 : 0}
            x={x(index)}
            y={HEIGHT - 8}
            // Chetdagi yorliq grafik ichida qoladi: markazlangan oxirgi
            // yorliqning yarmi o'ng chegaradan chiqib ketardi.
            textAnchor={
              data.length > 1 && index === data.length - 1
                ? "end"
                : data.length > 1 && index === 0
                  ? "start"
                  : "middle"
            }
            className="fill-muted-foreground text-[11px] tabular-nums"
          >
            {item.label}
          </text>
        ))}
      </svg>

      <p className="text-muted-foreground min-h-5 text-sm" aria-live="polite">
        {current
          ? [`${current.label} — ${valueText(current.value)}`, current.detail]
              .filter(Boolean)
              .join(" · ")
          : t("REPORTS.HOVER_HINT")}
      </p>
    </div>
  );
};
